/**
 * What Ask DANI is told about the crosstab on screen, beyond its figures (M4S-5777 X122, X128, L5).
 *
 * - `daniStatsContext`: the statistics the last Run used, in the same words as the Excel and
 *   PowerPoint legend (`buildSigLegendLines`, sig-confidence.ts), plus weighting, so DANI's answer
 *   and the file can never disagree about the test (X122).
 * - `withFiguresOncePerRun`: the engine's figures ride the first send after each Run in a
 *   conversation, and later sends say they are unchanged instead of sending them again (L5).
 * - `daniSourcesLine`: the questions, columns, filters, base and test the next answer reads, shown
 *   under the composer from exactly what is sent (X128).
 *
 * Pure.
 */
import type { ComputedTable } from '@/app/(private)/ai/components/Crosstabs/data';
import { buildSigLegendLines } from '@/app/(private)/ai/components/Crosstabs/sig-confidence';
import { weightingDisclosureLines } from '@/lib/crosstab/weight-scheme';

export interface DaniStatsContext {
    /**
     * The legend's own lines (tests, levels, what is compared, low bases, index, the standard) and
     * the weighting disclosure, as every exported file prints them, joined.
     */
    line: string;
    confidence: number | null;
    confidence2: number | null;
    sigScope: string | null;
    weighted: boolean;
}

/**
 * The last Run's statistics for DANI, in one wording with the files: the legend is
 * `buildSigLegendLines` given what the exports give it (`exportSettingsFor` and the display keys),
 * and weighting is `weightingDisclosureLines` of the computed table. `run` is the last Run's.
 */
export function daniStatsContext(
    run: {
        sigStyle?: string;
        confidence?: number | null;
        confidence2?: number | null;
        sigScope?: string;
        sigCorrection?: unknown;
        lowBaseThreshold?: number | null;
        suppressionEnabled?: boolean;
        showIndex?: boolean;
    },
    {
        computed,
        standard,
    }: { computed: Pick<ComputedTable, 'weightDiagnostics'>; standard?: string | null },
): DaniStatsContext {
    const legend = buildSigLegendLines({ ...run, statsStandard: standard ?? null })
        .map((l) => l.trim())
        .filter(Boolean)
        .filter((l) => l !== 'Significance testing:' && l !== 'Notes on the figures:');
    const parts = legend.length ? legend : ['No significance testing.'];
    parts.push(...weightingDisclosureLines(computed));
    const w = computed.weightDiagnostics;
    return {
        line: parts.join(' '),
        confidence: run.confidence ?? null,
        confidence2: run.confidence2 ?? null,
        sigScope: run.sigScope ?? null,
        weighted: !!w && !w.skipped,
    };
}

/** The note a send carries instead of figures DANI already has for this Run. */
export const FIGURES_UNCHANGED_NOTE =
    'The table’s figures are unchanged since they were sent earlier in this conversation (no Run since). Quote those.';

/**
 * The page data for one send: the table's figures only when this conversation has not had this
 * Run's yet. `sent` holds `<conversation>:<runId>` for every pair already sent and is updated.
 */
export function withFiguresOncePerRun(
    pageData: Record<string, unknown>,
    conversationId: string,
    sent: Set<string>,
): Record<string, unknown> {
    const runId = pageData.runId;
    if (!('table' in pageData) || (typeof runId !== 'string' && typeof runId !== 'number'))
        return pageData;
    const key = `${conversationId}:${runId}`;
    if (!sent.has(key)) {
        sent.add(key);
        return pageData;
    }
    const rest = { ...pageData };
    delete rest.table;
    return { ...rest, tableNote: FIGURES_UNCHANGED_NOTE };
}

type Named = { qid: string; question?: string };

const list = (items: readonly Named[], max = 6) => {
    const ids = items.map((i) => i.qid);
    return ids.length > max
        ? `${ids.slice(0, max).join(', ')} and ${ids.length - max} more`
        : ids.join(', ');
};

/**
 * The sources of DANI's next answer, from the page data that will be sent: rows, banner, the
 * filter, the base and the test. Null when the crosstab has no table yet.
 */
export function daniSourcesLine(pageData: Record<string, unknown>): string | null {
    const rows = (pageData.activeQuestions as Named[] | undefined) ?? [];
    const banner = (pageData.activeBanner as Named[] | undefined) ?? [];
    if (!rows.length && !banner.length) return null;
    const parts = [
        rows.length ? `Rows ${list(rows)}` : null,
        banner.length ? `banner ${list(banner)}` : null,
        typeof pageData.filteredTo === 'string' && pageData.filteredTo
            ? `filtered: ${pageData.filteredTo}`
            : 'no filter',
        typeof pageData.totalRespondents === 'number'
            ? `base ${pageData.totalRespondents.toLocaleString('en-US')}`
            : null,
        (pageData.statistics as DaniStatsContext | undefined)?.line ?? null,
    ].filter(Boolean);
    return `DANI reads the last Run: ${parts.join(' · ')}`;
}
